<template>

    <!-- Ligne normale -->
    <tr v-if="editMode === null">

        <!-- Utilisateur -->
        <td>
            <div class="d-flex align-items-center">

                <font-awesome-icon
                    :icon="['fas', 'user']"
                    class="me-1"
                />

                <div>
                    <div class="h6 mb-0 lh-1">
                        {{ user.email }}
                    </div>
                </div>

            </div>
        </td>

        <!-- Dernière connexion -->
        <td v-if="user.lastConnection">
            {{ $filters.secondDateFormat(user.lastConnection) }}

            <br>

            {{ $filters.timeAgo(user.lastConnection) }}
        </td>

        <td v-else>
            Jamais
        </td>

        <!-- Rôle -->
        <td>
            {{ roleDisplay }}
        </td>

        <!-- Visibilité directement modifiable -->
        <td>
            <select
                v-model="vehicleVisibility"
                class="form-select"
                :disabled="visibilityUpdating"
                @change="updateVisibility"
            >
                <option value="ALL">
                    Tous les véhicules
                </option>

                <option value="GROUPS">
                    Groupes uniquement
                </option>
            </select>
        </td>

        <!-- Groupes -->
        <td>

            <!-- Aucun affichage en ALL -->
            <template v-if="vehicleVisibility === 'GROUPS'">

                <div
                    v-for="group in user.groups"
                    :key="group.id"
                    class="badge bg-secondary text-white text-decoration-none small text-wrap mx-1"
                >
                    {{ group.name }}
                </div>

                <span
                    v-if="!user.groups || user.groups.length === 0"
                    class="text-muted"
                >
                    Aucun groupe
                </span>

            </template>

        </td>

        <!-- Menu -->
        <td class="text-end">

            <div>

                <a
                    data-bs-toggle="dropdown"
                    href="#"
                    class="btn p-1"
                    aria-expanded="false"
                >
                    <font-awesome-icon
                        :icon="['fas', 'bars']"
                    />
                </a>

                <div class="dropdown-menu dropdown-menu-end">

                    <a
                        href="#!"
                        class="dropdown-item"
                        @click.prevent="openRole"
                    >
                        Modifier les droits
                    </a>

                    <!-- Gestion des groupes uniquement en GROUPS -->
                    <a
                        v-if="vehicleVisibility === 'GROUPS'"
                        href="#!"
                        class="dropdown-item"
                        @click.prevent="openGroups"
                    >
                        Modifier les groupes
                    </a>

                    <a
                        href="#!"
                        class="dropdown-item"
                        @click.prevent="openPassword"
                    >
                        Changer le mot de passe
                    </a>

                    <a
                        href="#!"
                        class="dropdown-item"
                        @click.prevent="deleteUser(user.id)"
                    >
                        Supprimer l'utilisateur
                    </a>

                </div>

            </div>

        </td>

    </tr>


    <!-- Modification rôle -->
    <tr v-else-if="editMode === 'role'">

        <td>
            <div class="d-flex align-items-center">

                <font-awesome-icon
                    :icon="['fas', 'user']"
                    class="me-1"
                />

                <div>
                    <div class="h6 mb-0 lh-1">
                        {{ user.email }}
                    </div>
                </div>

            </div>
        </td>

        <td
            class="text-center"
            colspan="5"
        >

            <form
                class="row g-3"
                @submit.prevent
            >

                <div class="col-auto">

                    <select
                        v-model="role"
                        class="form-select"
                    >
                        <option value="clientviewer">
                            Visiteur
                        </option>

                        <option value="clientoperator">
                            Utilisateur
                        </option>

                        <option value="clientadmin">
                            Administrateur
                        </option>
                    </select>

                </div>

                <div class="col-auto">

                    <button
                        class="btn btn-primary btn-block"
                        @click="updateRole(user.id)"
                    >
                        Valider
                    </button>

                </div>

                <div class="col-auto">

                    <button
                        class="btn btn-danger btn-block"
                        @click="cancelEdit"
                    >
                        Annuler
                    </button>

                </div>

            </form>

        </td>

    </tr>


    <!-- Modification mot de passe -->
    <tr v-else-if="editMode === 'password'">

        <td>
            <div class="d-flex align-items-center">

                <font-awesome-icon
                    :icon="['fas', 'user']"
                    class="me-1"
                />

                <div>
                    <div class="h6 mb-0 lh-1">
                        {{ user.email }}
                    </div>
                </div>

            </div>
        </td>

        <td
            class="text-center"
            colspan="5"
        >

            <form
                class="row g-3"
                @submit.prevent
            >

                <div class="col-auto">

                    <input
                        v-model="password"
                        type="password"
                        class="form-control"
                        placeholder="Nouveau mot de passe"
                    >

                </div>

                <div class="col-auto">

                    <button
                        class="btn btn-primary btn-block"
                        @click="updatePassword(user.id)"
                    >
                        Valider
                    </button>

                </div>

                <div class="col-auto">

                    <button
                        class="btn btn-danger btn-block"
                        @click="cancelEdit"
                    >
                        Annuler
                    </button>

                </div>

            </form>

        </td>

    </tr>


    <!-- Modification groupes -->
    <tr v-else-if="editMode === 'groups'">

        <td>
            <div class="d-flex align-items-center">

                <font-awesome-icon
                    :icon="['fas', 'user']"
                    class="me-1"
                />

                <div>
                    <div class="h6 mb-0 lh-1">
                        {{ user.email }}
                    </div>
                </div>

            </div>
        </td>

        <td colspan="5">

            <div class="row g-3 align-items-center">

                <div
                    v-for="group in groups"
                    :key="group.id"
                    class="form-check col-auto"
                >

                    <input
                        :id="'user-' + user.id + '-group-' + group.id"
                        v-model="groupSelected"
                        :value="group.id"
                        type="checkbox"
                        class="form-check-input"
                    >

                    <label
                        class="form-check-label"
                        :for="'user-' + user.id + '-group-' + group.id"
                    >
                        {{ group.name }}
                    </label>

                </div>

                <div class="col-auto">

                    <button
                        class="btn btn-primary btn-block"
                        @click="updateGroups"
                    >
                        Valider
                    </button>

                </div>

                <div class="col-auto">

                    <button
                        class="btn btn-danger btn-block"
                        @click="cancelEdit"
                    >
                        Annuler
                    </button>

                </div>

            </div>

        </td>

    </tr>

</template>

<script>
import UserService from '@/services/user.service'
import AuthService from '@/services/auth.service'

export default {

    props: {
        user: {
            type: Object,
            required: true
        },

        groups: {
            type: Array,
            default: () => []
        }
    },

    emits: ['refresh'],

    data() {
        return {
            editMode: null,

            role:
                this.user?.role?.name ||
                'clientviewer',

            password: '',

            vehicleVisibility:
                this.user?.vehicleVisibility ||
                'ALL',

            groupSelected:
                (this.user?.groups || [])
                    .map(group => group.id),

            visibilityUpdating: false
        };
    },

    computed: {

        roleDisplay() {

            switch (this.user?.role?.name) {

                case 'clientadmin':
                    return 'Administrateur';

                case 'clientoperator':
                    return 'Utilisateur';

                case 'clientviewer':
                    return 'Visiteur';

                default:
                    return '';
            }
        }

    },

    watch: {

        'user.vehicleVisibility'(value) {
            this.vehicleVisibility =
                value || 'ALL';
        },

        'user.groups': {
            deep: true,

            handler(groups) {
                this.groupSelected =
                    (groups || [])
                        .map(group => group.id);
            }
        }

    },

    methods: {

        openRole() {

            this.role =
                this.user?.role?.name ||
                'clientviewer';

            this.editMode = 'role';
        },

        openPassword() {

            this.password = '';

            this.editMode = 'password';
        },

        openGroups() {

            /*
             * Les groupes ne sont modifiables
             * qu'en mode GROUPS.
             */
            if (this.vehicleVisibility !== 'GROUPS') {
                return;
            }

            this.groupSelected =
                (this.user.groups || [])
                    .map(group => group.id);

            this.editMode = 'groups';
        },

        cancelEdit() {

            this.role =
                this.user?.role?.name ||
                'clientviewer';

            this.password = '';

            this.groupSelected =
                (this.user.groups || [])
                    .map(group => group.id);

            this.editMode = null;
        },

        deleteUser(uId) {

            if (!confirm("Supprimer l'utilisateur ?")) {
                return;
            }

            UserService.deleteUser(uId)
                .then(() => {
                    this.$emit('refresh');
                })
                .catch(e => {
                    console.log(e);
                });
        },

        updatePassword(uId) {

            AuthService.updatePassword(
                uId,
                this.password
            )
                .then(() => {

                    this.password = '';
                    this.editMode = null;

                    this.$emit('refresh');

                })
                .catch(e => {
                    console.log(e);
                });
        },

        updateRole(uId) {

            UserService.updateRole(
                uId,
                this.role
            )
                .then(() => {

                    this.editMode = null;

                    this.$emit('refresh');

                })
                .catch(e => {
                    console.log(e);
                });
        },

        async updateVisibility() {

            if (this.visibilityUpdating) {
                return;
            }

            const previousVisibility =
                this.user.vehicleVisibility ||
                'ALL';

            this.visibilityUpdating = true;

            try {

                await UserService.updateVehicleVisibility(
                    this.user.id,
                    this.vehicleVisibility
                );

                /*
                 * Aucun updateGroups ici.
                 *
                 * Les groupes existants restent donc en BDD
                 * lors d'un passage GROUPS -> ALL.
                 */
                this.$emit('refresh');

            } catch (e) {

                console.log(e);

                /*
                 * En cas d'erreur on remet la valeur serveur.
                 */
                this.vehicleVisibility =
                    previousVisibility;

            } finally {

                this.visibilityUpdating = false;
            }
        },

        updateGroups() {

            if (this.vehicleVisibility !== 'GROUPS') {
                return;
            }

            UserService.updateGroups(
                this.user.id,
                this.groupSelected
            )
                .then(() => {

                    this.editMode = null;

                    this.$emit('refresh');

                })
                .catch(e => {
                    console.log(e);
                });
        }

    }
}
</script>